import { Injectable } from '@angular/core';

// Registers assets/offline-video-sw.js (see that file for why this mechanism and not
// MediaSource) and builds the virtual URL a plain <video> element's src can point at.
// Separate from OfflineMediaService's readChunksForPlayback generator, which exists for a
// hypothetical MSE path but is NOT what P5 actually uses for video — see
// offline-video-sw.js's header comment.
@Injectable({
  providedIn: 'root',
})
export class OfflineVideoPlaybackService {
  private static readonly SW_URL = '/assets/offline-video-sw.js';
  private static readonly SW_SCOPE = '/assets/offline-video/';

  private registrationPromise: Promise<ServiceWorkerRegistration> | null = null;

  // Installs the worker (and, via its install handler, precaches player.html) and lets the
  // browser run its update check. Callers use this to get the worker in place WHILE THERE IS
  // STILL A NETWORK - a worker that is only registered on the offline playback path can
  // never install itself in time, and once installed could never fetch a newer script,
  // because by then there is no network. Never throws.
  async ensureReady(): Promise<boolean> {
    try {
      await this.ensureRegistered();
      return true;
    } catch (err) {
      console.error('[offline] video service worker unavailable', err);
      return false;
    }
  }

  private async ensureRegistered(): Promise<ServiceWorkerRegistration> {
    if (!navigator.serviceWorker) {
      throw new Error('serviceWorker unsupported in this browser');
    }
    if (!this.registrationPromise) {
      this.registrationPromise = navigator.serviceWorker
        .register(OfflineVideoPlaybackService.SW_URL, { scope: OfflineVideoPlaybackService.SW_SCOPE })
        .then(async (reg) => {
          await this.waitForActive(reg);
          return reg;
        })
        .catch((err) => {
          this.registrationPromise = null;
          throw err;
        });
    }
    return this.registrationPromise;
  }

  private waitForActive(reg: ServiceWorkerRegistration): Promise<void> {
    return new Promise((resolve) => {
      if (reg.active) return resolve();
      const sw = reg.installing || reg.waiting;
      if (!sw) return resolve();
      sw.addEventListener('statechange', () => {
        if (sw.state === 'activated') resolve();
      });
    });
  }

  // Resolves once the SW is ready, then returns the URL to put in an IFRAME - not a <video
  // src>. It has to be an iframe pointing at a page inside SW_SCOPE, because a service
  // worker's scope decides which pages it controls, not which URLs it may intercept. The
  // app's own pages are at "/" and are therefore controlled by Angular's ngsw-worker.js, so
  // a <video src=".../offline-video/virtual"> on an app page never reaches this worker at
  // all - it goes to the network and hangs forever offline. player.html lives in scope, so
  // the <video> inside it does reach this worker. See assets/offline-video/player.html.
  //
  // Throws if Service Workers aren't supported — caller must have already checked
  // offlineVideoSupported() before offering offline playback at all.
  async getPlaybackUrl(school: string, user: number, resourceId: string, rendition: string, version: string): Promise<string> {
    await this.ensureRegistered();
    const params = new URLSearchParams({ school, user: String(user), resourceId, rendition, version });
    return `${OfflineVideoPlaybackService.SW_SCOPE}player.html?${params.toString()}`;
  }
}
